import { useEffect, useState } from "react";
import { Row, Col, Card, Button, Select, InputNumber, Input, DatePicker, Space, Form, message, Alert, Badge, Progress } from "antd";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import { getStockProducto } from "../../../../services/financiero/finEntregasService";

import dayjs from "dayjs";

const { Option } = Select;

const createEmptyRow = () => ({
  key: Date.now() + Math.random(),
  oid_participante: null,
  oid_producto: null,
  cantidad: null,
  oid_departamento: null,
  oid_municipio: null,
  oid_proyecto: null,
  carpeta: "",
  nombre_archivo: "",
  fecha_entrega: dayjs(),
  tipo_entrega: 1
});

export default function EntregasBulkForm({ participantes = [], productos = [], municipios = [], departamentos = [], proyectos = [], onChange, onValidationChange }) {

  const [rows, setRows] = useState([createEmptyRow()]);
  const [stockMap, setStockMap] = useState({});
  const [rowErrors, setRowErrors] = useState({});
  const getMunicipioDeptoId = (m) =>
    m.oid_departamento ?? m.oid_depto ?? m.departamento_id ?? null;

  const emitChanges = (updatedRows, errors = rowErrors) => {
    onChange?.(updatedRows);

    const isValid =
      updatedRows.length > 0 &&
      updatedRows.every((row) => {
        const errs = errors[row.key] || {};
        return Object.keys(errs).length === 0;
      });

    onValidationChange?.({
      isValid,
      rows: updatedRows,
      errors
    });
  };

  const getReservedByProduct = (productoId, excludeRowKey = null) => {
    if (!productoId) return 0;
    return rows.reduce((acc, row) => {
      if (
        row.key !== excludeRowKey &&
        Number(row.oid_producto) === Number(productoId)
      ) {
        return acc + Number(row.cantidad || 0);
      }
      return acc;
    }, 0);
  };

  const validateRow = (row, currentRows = rows) => {
    const errors = {};
    if (!row.oid_participante) {
      errors.oid_participante = "Debe seleccionar el participante.";
    }
    if (!row.oid_proyecto) {
      errors.oid_proyecto = "Debe seleccionar el proyecto.";
    }
    if (!row.oid_departamento) {
      errors.oid_departamento = "Debe seleccionar el departamento.";
    }
    if (!row.oid_municipio) {
      errors.oid_municipio = "Debe seleccionar el municipio.";
    }
    if (!row.oid_producto) {
      errors.oid_producto = "Debe seleccionar el producto.";
    }
    if (!row.tipo_entrega) {
      errors.tipo_entrega = "Debe seleccionar el tipo de entrega.";
    }
    if (!row.fecha_entrega) {
      errors.fecha_entrega = "Debe seleccionar la fecha de entrega.";
    }
    if (!row.cantidad || Number(row.cantidad) <= 0) {
      errors.cantidad = "La cantidad debe ser mayor que cero.";
    }
    if (row.oid_municipio && row.oid_departamento) {
      const municipio = municipios.find(
        (m) => Number(m.oid) === Number(row.oid_municipio)
      );
      const deptoMunicipio = municipio ? getMunicipioDeptoId(municipio) : null;
      if (
        municipio &&
        Number(deptoMunicipio) !== Number(row.oid_departamento)
      ) {
        errors.oid_municipio =
          "El municipio seleccionado no pertenece al departamento escogido.";
      }
    }
    if (row.oid_producto && row.cantidad) {
      const stockBd = Number(stockMap[row.key] || 0);
      const reservedInOtherRows = currentRows.reduce((acc, item) => {
        if (
          item.key !== row.key &&
          Number(item.oid_producto) === Number(row.oid_producto)
        ) {
          return acc + Number(item.cantidad || 0);
        }
        return acc;
      }, 0);
      const available = Math.max(stockBd - reservedInOtherRows, 0);
      if (available <= 0) {
        errors.cantidad =
          "Este producto ya no tiene stock disponible en las demás entregas del formulario.";
      } else if (Number(row.cantidad) > available) {
        errors.cantidad = `Stock insuficiente. Disponible para esta entrega: ${available}.`;
      }
    }
    return errors;
  };

  const validateAllRows = (currentRows = rows) => {
    const newErrors = {};
    currentRows.forEach((row) => {
      const errs = validateRow(row, currentRows);
      newErrors[row.key] = errs;
    });
    setRowErrors(newErrors);
    emitChanges(currentRows, newErrors);
  };

  const updateRow = (key, field, value) => {
    const updated = rows.map((row) =>
      row.key === key ? { ...row, [field]: value } : row
    );
    setRows(updated);
  };

  const handleDepartamentoChange = (rowKey, departamentoId) => {
    const updated = rows.map((row) => {
      if (row.key !== rowKey) return row;
      return {
        ...row,
        oid_departamento: departamentoId,
        oid_municipio: null
      };
    });
    setRows(updated);
  };

  const handleProductoChange = async (rowKey, productoId) => {
    const updated = rows.map((row) =>
      row.key === rowKey
        ? {
          ...row,
          oid_producto: productoId,
          cantidad: 1
        }
        : row
    );
    setRows(updated);
    try {
      const stock = await getStockProducto(productoId);
      const stockDisponible = Number(stock.stock_disponible || 0);
      if (stockDisponible === 0) {
        message.warning("Este producto no tiene stock disponible.");
      }
      setStockMap((prev) => ({
        ...prev,
        [rowKey]: stockDisponible
      }));
    } catch (error) {
      message.error("No se pudo consultar el stock del producto.");
    }
  };

  const handleCantidadChange = (rowKey, cantidad) => {
    const row = rows.find(r => r.key === rowKey);
    if (!row || !row.oid_producto) return;
    const stockBd = Number(stockMap[rowKey] || 0);
    const reservedInOtherRows = getReservedByProduct(row.oid_producto, rowKey);
    const available = Math.max(stockBd - reservedInOtherRows, 0);
    if (cantidad > available) {
      message.warning(
        `No hay suficiente stock disponible. Solo quedan ${available} unidades para este producto.`
      );
      cantidad = available;
    }
    if (available === 0) {
      message.error(
        "Este producto ya está completamente reservado en otras entregas."
      );
      cantidad = 0;
    }
    const updated = rows.map((row) =>
      row.key === rowKey ? { ...row, cantidad } : row
    );
    setRows(updated);
  };
  const addRow = () => {
    const totalReserved = rows.reduce((acc, r) => {
      return acc + Number(r.cantidad || 0);
    }, 0);
    const totalStock = Object.values(stockMap).reduce((a, b) => a + b, 0);
    if (totalReserved >= totalStock && totalStock > 0) {
      message.warning("Todo el stock disponible ya está reservado.");
      return;
    }
    const updated = [...rows, createEmptyRow()];
    setRows(updated);
  };
  const removeRow = (key) => {
    if (rows.length === 1) return;
    const updated = rows.filter((row) => row.key !== key);
    const newStockMap = { ...stockMap };
    delete newStockMap[key];
    setStockMap(newStockMap);
    const newErrors = { ...rowErrors };
    delete newErrors[key];
    setRowErrors(newErrors);
    setRows(updated);
  };

  useEffect(() => {
    validateAllRows(rows);
  }, [rows, stockMap]);


  return (
    <Space direction="vertical" style={{ width: "100%" }} size={20}>
      <Alert
        type="info"
        banner
        showIcon
        message="Puede registrar varias entregas. El sistema valida stock y consistencia automáticamente."
      />
      <Button
        type="dashed"
        icon={<PlusOutlined />}
        onClick={addRow}
        style={{ width: "100%" }}
      >
        Agregar entrega
      </Button>
      {rows.map((row, index) => {
        const errors = rowErrors[row.key] || {};
        const filteredMunicipios = municipios.filter(
          (m) =>
            Number(getMunicipioDeptoId(m)) === Number(row.oid_departamento)
        );
        const stockBd = Number(stockMap[row.key] || 0);
        const reservedInOtherRows = getReservedByProduct(row.oid_producto, row.key);
        const availableStock = Math.max(stockBd - reservedInOtherRows, 0);
        const porcentajeUso = stockBd
          ? Math.round((reservedInOtherRows / stockBd) * 100)
          : 0;
        const hasErrors = Object.keys(errors).length > 0;
        return (
          <Card
            size="small"
            key={row.key}
            title={
              <span style={{ color: "#FFFFFF" }}>
                {`Entrega ${index + 1}`}
              </span>
            }
            extra={
              <Button
                type="text"
                icon={
                  <DeleteOutlined
                    style={{
                      color: "#FFFFFF",
                      fontSize: "18px"
                    }}
                  />
                }
                onClick={() => removeRow(row.key)}
              />
            }
            styles={{
              header: {
                background: "#042956",
                borderBottom: "none"
              }
            }}
          >
            <Form layout="vertical" style={{ marginTop: 10 }}>
              {hasErrors && (
                <Alert
                  type="error"
                  showIcon
                  message="Campos pendientes"
                  style={{ marginBottom: 10 }}
                />
              )}

              {availableStock === 0 && (
                <Alert
                  type="warning"
                  showIcon
                  message="Stock agotado"
                  description="Todo el stock de este producto ya fue asignado en otras entregas."
                  style={{ marginTop: 8 }}
                />
              )}
              <Row gutter={[16, 16]}>
                <Col xs={24} md={12} lg={8}>
                  <Form.Item
                    label="Participante"
                    required
                    validateStatus={errors.oid_participante ? "error" : ""}
                    help={errors.oid_participante}
                  >
                    <Select
                      showSearch
                      placeholder="Seleccione participante"
                      value={row.oid_participante}
                      onChange={(v) => updateRow(row.key, "oid_participante", v)}
                      optionFilterProp="children"
                    >
                      {participantes.map((p) => (
                        <Option key={p.oid} value={p.oid}>
                          {p.nombre_participante} - {p.numero_identificacion}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={24} md={12} lg={8}>
                  <Form.Item
                    label="Proyecto"
                    required
                    validateStatus={errors.oid_proyecto ? "error" : ""}
                    help={errors.oid_proyecto}
                  >
                    <Select
                      showSearch
                      placeholder="Seleccione proyecto"
                      value={row.oid_proyecto}
                      onChange={(v) => updateRow(row.key, "oid_proyecto", v)}
                      optionFilterProp="children"
                    >
                      {proyectos.map((p) => (
                        <Option key={p.oid} value={p.oid}>
                          {p.codigo} - {p.proyecto}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={24} md={12} lg={8}>
                  <Form.Item
                    label="Departamento"
                    required
                    validateStatus={errors.oid_departamento ? "error" : ""}
                    help={errors.oid_departamento}
                  >
                    <Select
                      showSearch
                      placeholder="Seleccione departamento"
                      value={row.oid_departamento}
                      onChange={(v) => handleDepartamentoChange(row.key, v)}
                      optionFilterProp="children"
                    >
                      {departamentos.map((d) => (
                        <Option key={d.oid} value={d.oid}>
                          {d.departamento}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={[16, 16]}>
                <Col xs={24} md={12} lg={8}>
                  <Form.Item
                    label="Municipio"
                    required
                    validateStatus={errors.oid_municipio ? "error" : ""}
                    help={
                      errors.oid_municipio ||
                      (!row.oid_departamento
                        ? "Primero seleccione un departamento."
                        : null)
                    }
                  >
                    <Select
                      showSearch
                      placeholder={
                        row.oid_departamento
                          ? "Seleccione municipio"
                          : "Seleccione primero un departamento"
                      }
                      value={row.oid_municipio}
                      disabled={!row.oid_departamento}
                      onChange={(v) => updateRow(row.key, "oid_municipio", v)}
                      optionFilterProp="children"
                    >
                      {filteredMunicipios.map((m) => (
                        <Option key={m.oid} value={m.oid}>
                          {m.municipio}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
                <Col xs={24} md={12} lg={8}>
                  <Form.Item
                    label="Producto"
                    required
                    validateStatus={errors.oid_producto ? "error" : ""}
                    help={errors.oid_producto}
                  >
                    <Select
                      showSearch
                      placeholder="Seleccione producto"
                      value={row.oid_producto}
                      onChange={(v) => handleProductoChange(row.key, v)}
                      optionFilterProp="children"
                    >
                      {productos.map((p) => (
                        <Option key={p.oid} value={p.oid}>
                          {p.nombre_producto}
                        </Option>
                      ))}
                    </Select>

                    {row.oid_producto && (
                      <div style={{ marginTop: 8 }}>
                        <Progress
                          percent={porcentajeUso}
                          size="small"
                          status={availableStock === 0 ? "exception" : "active"}
                          format={() => `${availableStock} disponibles`}
                        />
                        <Badge
                          status={availableStock > 0 ? "success" : "error"}
                          text={`Disponible para esta entrega: ${availableStock}`}
                        />
                      </div>
                    )}
                  </Form.Item>
                </Col>
                <Col xs={24} md={12} lg={4}>
                  <Form.Item
                    label="Cantidad"
                    required
                    validateStatus={errors.cantidad ? "error" : ""}
                    help={errors.cantidad}
                  >
                    <InputNumber
                      min={1}
                      max={availableStock || 1}
                      style={{ width: "100%" }}
                      value={row.cantidad}
                      disabled={!row.oid_producto || availableStock <= 0}
                      onChange={(v) => handleCantidadChange(row.key, v)}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12} lg={4}>
                  <Form.Item
                    label="Tipo"
                    required
                    validateStatus={errors.tipo_entrega ? "error" : ""}
                    help={errors.tipo_entrega}
                  >
                    <Select
                      value={row.tipo_entrega}
                      onChange={(v) => updateRow(row.key, "tipo_entrega", v)}
                    >
                      <Option value={1}>Participante</Option>
                      <Option value={2}>Organización</Option>
                    </Select>
                  </Form.Item>
                </Col>
              </Row>
              <Row gutter={[16, 16]}>
                <Col xs={24} md={8}>
                  <Form.Item
                    label="Fecha entrega"
                    required
                    validateStatus={errors.fecha_entrega ? "error" : ""}
                    help={errors.fecha_entrega}
                  >
                    <DatePicker
                      style={{ width: "100%" }}
                      value={row.fecha_entrega}
                      onChange={(v) => updateRow(row.key, "fecha_entrega", v)}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item label="Carpeta">
                    <Input
                      placeholder="Opcional"
                      value={row.carpeta}
                      onChange={(e) =>
                        updateRow(row.key, "carpeta", e.target.value)
                      }
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item label="Nombre archivo">
                    <Input
                      placeholder="Opcional"
                      value={row.nombre_archivo}
                      onChange={(e) =>
                        updateRow(row.key, "nombre_archivo", e.target.value)
                      }
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Form>
          </Card>
        );
      })}
    </Space>
  );
}